import { lazy } from "react";

const RELOAD_KEY = "chunkReloadAt";
const RELOAD_COOLDOWN_MS = 60_000;
const RETRY_DELAY_MS = 400;
const RELOAD_TIMEOUT_MS = 10_000;

const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|Loading chunk \S+ failed/i;

const isChunkLoadError = (error) =>
  error?.name === "ChunkLoadError" ||
  CHUNK_ERROR_PATTERN.test(error?.message || "");

const lastReloadAt = () => {
  try {
    return Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
  } catch {
    return Date.now();
  }
};

const markReloadAttempt = () => {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {}
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const reloadOnce = () => {
  if (navigator.onLine === false) return false;
  if (Date.now() - lastReloadAt() < RELOAD_COOLDOWN_MS) return false;

  markReloadAttempt();
  window.location.reload();

  return true;
};

/*
 * Keeps Suspense in its fallback while the page tears down, but surfaces the
 * original error if the reload never happens, instead of spinning forever.
 */
const pendingUntilReload = (error) =>
  new Promise((_, reject) =>
    setTimeout(() => reject(error), RELOAD_TIMEOUT_MS)
  );

let pendingImports = 0;

/*
 * A network blip looks exactly like a missing chunk, so retry once before
 * reloading - a retry keeps the filters, forms and scroll position a reload
 * throws away.
 */
const load = (factory) => {
  pendingImports += 1;

  return factory()
    .catch((error) => {
      if (!isChunkLoadError(error)) throw error;

      return wait(RETRY_DELAY_MS)
        .then(factory)
        .catch(() => {
          if (!reloadOnce()) throw error;

          return pendingUntilReload(error);
        });
    })
    .finally(() => {
      pendingImports -= 1;
    });
};

export const lazyWithReload = (factory) => lazy(() => load(factory));

/*
 * Safety net for dynamic imports we don't wrap ourselves (dependency-internal
 * ones). Vite dispatches this for every failed chunk preload, including the
 * ones load() is already recovering from, so stay out of its way while one is
 * in flight. Deliberately no preventDefault(): Vite would then resolve the
 * import with undefined and React.lazy would render nothing.
 */
export const watchChunkErrors = () => {
  window.addEventListener("vite:preloadError", () => {
    if (pendingImports > 0) return;

    reloadOnce();
  });
};
